import type { CSSProperties } from 'react'
import { Mention, MentionsInput } from 'react-mentions'
import type { MentionItem, MentionsInputProps, MentionsInputStyle } from 'react-mentions'

import { cn } from '@/lib/utils.ts'

import { useQueryProjectMembers } from '@/requests/queries/useQueryProject.ts'

// biome-ignore lint/suspicious/noExplicitAny: Needed for react-mentions type compatibility
const _MentionComponent = Mention as any

// biome-ignore lint/suspicious/noExplicitAny: Needed for react-mentions type compatibility
const _MentionInputComponent = MentionsInput as any

type Props = Omit<MentionsInputProps, 'children'> & {
  markup: string
}

export const MentionInputComponent = (props: Props) => {
  const { members } = useQueryProjectMembers()

  const fetchUsers = (
    query: string,
    callback: (users: Array<{ id: string; display: string; name: string; username?: string }>) => void,
  ) => {
    const activeMembers = members.filter((m) => m.status === 'ACTIVE')
    const filteredUsers = activeMembers
      .filter(
        (member) =>
          member.name.toLowerCase().includes(query.toLowerCase()) ||
          member.username?.toLowerCase().includes(query.toLowerCase()),
      )
      .map((member) => ({
        id: member.id,
        display: member.username || member.name,
        name: member.name,
        username: member.username,
      }))

    filteredUsers.sort((a, b) => {
      const aUsernameMatch = a.username?.toLowerCase().includes(query.toLowerCase()) || false
      const bUsernameMatch = b.username?.toLowerCase().includes(query.toLowerCase()) || false
      if (aUsernameMatch && !bUsernameMatch) return -1
      if (!aUsernameMatch && bUsernameMatch) return 1
      return 0
    })

    callback(filteredUsers)
  }

  return (
    <_MentionInputComponent {...props}>
      <_MentionComponent
        trigger="@"
        data={fetchUsers}
        renderSuggestion={(suggestion: MentionItem & { name: string; username?: string }, search: string) => {
          const nameMatch = suggestion.name.toLowerCase().includes(search.toLowerCase())
          const usernameMatch = suggestion.username?.toLowerCase().includes(search.toLowerCase())

          return (
            <div className="flex flex-col py-1">
              <div className="flex flex-row items-center gap-2">
                <span className={cn('font-EN-KR-body2-bold', nameMatch && 'text-blue-600')}>{suggestion.name}</span>
                {suggestion.username && (
                  <span className={cn('text-sm text-contents-secondary', usernameMatch && 'text-blue-600')}>
                    @{suggestion.username}
                  </span>
                )}
              </div>
            </div>
          )
        }}
        displayTransform={(_: string, display: string) => `@${display}`}
        markup={props.markup}
        style={{
          backgroundColor: 'transparent',
          padding: 0,
          color: 'inherit',
        }}
        appendSpaceOnAdd
      />
    </_MentionInputComponent>
  )
}

export const mentionSuggestionsStyle: MentionsInputStyle = {
  suggestions: {
    list: {
      backgroundColor: 'white',
      border: '1px solid rgba(0,0,0,0.15)',
      fontSize: 'inherit',
      borderRadius: '4px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      zIndex: 50,
      maxHeight: '150px',
      overflowY: 'auto',
    },
    item: {
      padding: '8px 12px',
      borderBottom: '1px solid rgba(0,0,0,0.05)',
      '&focused': {
        backgroundColor: '#E8F1FF',
      },
    },
  },
}

export const getMentionSuggestionsStyle = ({
  fontSize = 'inherit',
  lineHeight = 'inherit',
  control = {},
  highlighter = {},
  input = {},
  suggestions = {},
}: {
  fontSize?: string
  lineHeight?: string
  control?: CSSProperties
  highlighter?: CSSProperties
  input?: CSSProperties
  suggestions?: {
    list?: CSSProperties
  }
}): MentionsInputStyle => ({
  highlighter: {
    padding: 0,
    lineHeight,
    fontSize: fontSize,
    boxSizing: 'border-box',
    ...highlighter,
  },
  input: {
    fontSize,
    lineHeight,
    ...input,
  },
  control,
  suggestions: {
    list: {
      backgroundColor: 'white',
      border: '1px solid rgba(0,0,0,0.15)',
      fontSize: 14,
      borderRadius: '4px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      zIndex: 50,
      maxHeight: '150px',
      overflowY: 'auto',
      ...suggestions.list,
    },
    item: {
      padding: '8px 12px',
      borderBottom: '1px solid rgba(0,0,0,0.05)',
      '&focused': {
        backgroundColor: '#E8F1FF',
      },
    },
  },
})
